/**
 * InventoryPanel.jsx - Spare Parts > Inventory: the parts we own and can
 * sell (Shawn: bought for a project and never used). Each stock row is a
 * part on a shelf: how many, where, its condition and unit cost, its value
 * (qty x unit cost) and the project it was bought for. The count changes
 * only through a move - in or out, how many and why (sold to ..., used on
 * ..., count correction) - so every change is traceable (the part's moves
 * are in its drawer). A row is removed only once it is empty.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Modal } from '../ui/Modal';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { createStock, deleteStock, errorOf, listInventory, moveStock, updateStock } from './sparePartsApi';
import { fmtDay, fmtMoney, projectLabel } from './sparePartsRules';
import { Fact, GREEN, LINE, MUTED, Msg, PriceChip, num, td, th } from './spUi';
import { Btn, Field, Grid, NumInput, SelectInput, TextInput, inputStyle } from '../quotes/quoteUi';

const CONDITIONS = ['New', 'New - open box', 'Used', 'Refurbished', 'Damaged'];
const STOCK_FIELDS = [
  'part_number',
  'location',
  'condition',
  'unit_cost',
  'origin_project_id',
  'origin_project_number',
  'received_at',
  'notes',
];

export default function InventoryPanel({ editable, onOpenPart, tick = 0 }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | stock row
  const [moving, setMoving] = useState(null); // stock row
  const reads = useRef(latestReads()).current;

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const ticket = reads.start();
      const res = await listInventory({ q: query, all });
      if (!reads.show(ticket)) return;
      if (!res.ok) {
        if (!quiet) setMsg({ kind: 'error', text: errorOf(res, 'Could not load the stock.') });
        return;
      }
      setData(res.body);
    },
    [query, all, reads]
  );
  useEffect(() => {
    load();
  }, [load, tick]);
  useLiveRefresh('spare-parts', () => load({ quiet: true }));

  const remove = async (row) => {
    if (
      !window.confirm(
        `Remove the empty stock row for ${row.part_number}${row.location ? ` at ${row.location}` : ''}? Its moves stay in the log.`
      )
    )
      return;
    const res = await deleteStock(row.id);
    setMsg(res.ok ? { kind: 'ok', text: 'Stock row removed.' } : { kind: 'error', text: errorOf(res, 'Could not remove the row.') });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <TextInput
          aria-label="Search stock"
          value={q}
          onChange={setQ}
          placeholder="Search part #, description, shelf, project..."
          style={{ maxWidth: 360 }}
        />
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 11.5, fontWeight: 600 }}>
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show empty rows
        </label>
        {editable && (
          <Btn primary small onClick={() => setEditing('new')} style={{ marginLeft: 'auto' }}>
            + Add stock
          </Btn>
        )}
      </div>
      <Msg msg={msg} />
      {!data && !msg && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the stock...</div>}
      {data && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 22,
              flexWrap: 'wrap',
              marginBottom: 10,
              padding: '10px 12px',
              background: '#FAFBF7',
              border: `1px solid ${LINE}`,
              borderRadius: 8,
            }}
          >
            <Fact label="Stock rows">{data.totals.rows}</Fact>
            <Fact label="Pieces on hand">
              <b>{data.totals.qty}</b>
            </Fact>
            <Fact label="Value at cost">
              <b>{fmtMoney(data.totals.value)}</b>
              {data.totals.uncosted ? (
                <span style={{ fontSize: 10.5, color: '#8D6E00', marginLeft: 6 }}>({data.totals.uncosted} without a unit cost)</span>
              ) : null}
            </Fact>
          </div>
          <div
            style={{ overflow: 'auto', maxHeight: 'calc(100vh - 330px)', border: `1px solid ${LINE}`, borderRadius: 8, background: '#fff' }}
          >
            <table aria-label="Stock on hand" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  {[
                    'Part #',
                    'Description',
                    'Location',
                    'Condition',
                    'On hand',
                    'Unit cost',
                    'Value',
                    'Bought for',
                    'Received',
                    'GLOBAL price',
                    '',
                  ].map((h, i) => (
                    <th key={h || i} style={{ ...th, textAlign: [4, 5, 6, 9].includes(i) ? 'right' : 'left' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid="stock-row">
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => onOpenPart(r.part_number)}
                        style={{ all: 'unset', cursor: 'pointer', color: GREEN, fontWeight: 700 }}
                      >
                        {r.part_number}
                      </button>
                    </td>
                    <td style={{ ...td, maxWidth: 260 }}>{r.description || '-'}</td>
                    <td style={td}>{r.location || '-'}</td>
                    <td style={td}>{r.condition}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{r.qty_on_hand}</td>
                    <td style={num}>{fmtMoney(r.unit_cost, r.currency)}</td>
                    <td style={num}>{fmtMoney(r.value, r.currency)}</td>
                    <td style={td}>{[r.origin_project_number, projectLabel(r)].filter(Boolean).join(' · ') || '-'}</td>
                    <td style={td}>{fmtDay(r.received_at)}</td>
                    <td style={num} title={`Price checked: ${r.price_age.label}`}>
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {fmtMoney(r.global_price)}
                        <PriceChip age={r.price_age} checkedAt={r.price_checked_at} compact />
                      </span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {editable && (
                        <span style={{ display: 'inline-flex', gap: 4 }}>
                          <Btn small onClick={() => setMoving(r)}>
                            In / out
                          </Btn>
                          <Btn small onClick={() => setEditing(r)}>
                            Edit
                          </Btn>
                          {r.qty_on_hand === 0 && (
                            <Btn small danger onClick={() => remove(r)}>
                              Remove
                            </Btn>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {!data.rows.length && (
                  <tr>
                    <td colSpan={11} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                      {query ? 'Nothing on the shelf matches.' : 'Nothing on the shelf yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {editing && (
        <StockEditor
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(text) => {
            setEditing(null);
            setMsg({ kind: 'ok', text });
            load();
          }}
        />
      )}
      {moving && (
        <MoveEditor
          row={moving}
          onClose={() => setMoving(null)}
          onSaved={(text) => {
            setMoving(null);
            setMsg({ kind: 'ok', text });
            load();
          }}
        />
      )}
    </div>
  );
}

/** The projects a stock row or a move can name, newest quote first. */
function useProjects() {
  const { allProjects } = useApp() || {};
  return useMemo(
    () =>
      (allProjects || [])
        .filter((p) => p && p.id)
        .sort((a, b) => String(b.quoteLetter || '').localeCompare(String(a.quoteLetter || ''), 'en', { numeric: true })),
    [allProjects]
  );
}

/** Add a stock row (its quantity logged as the first move in) or edit one (everything but the count). */
function StockEditor({ row, onClose, onSaved }) {
  const projects = useProjects();
  const [draft, setDraft] = useState(() => ({
    ...Object.fromEntries(STOCK_FIELDS.map((k) => [k, row && row[k] !== null && row[k] !== undefined ? String(row[k]) : ''])),
    condition: (row && row.condition) || 'New',
    qty_on_hand: '',
    reason: '',
  }));
  const [msg, setMsg] = useState(null);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    const body = {};
    for (const k of STOCK_FIELDS) {
      const now = String(draft[k] || '').trim();
      if (!row || now !== String(row[k] ?? '')) body[k] = now || null;
    }
    if (!row) {
      body.qty_on_hand = draft.qty_on_hand || 0;
      if (draft.reason.trim()) body.reason = draft.reason.trim();
    }
    const res = row ? await updateStock(row.id, body) : await createStock(body);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not save the stock row.') });
      return;
    }
    onSaved(row ? 'Stock row saved.' : `${res.body.qty_on_hand} x ${res.body.part_number} on the shelf.`);
  };
  return (
    <Modal
      title={row ? `Stock: ${row.part_number}` : 'Add stock'}
      onClose={onClose}
      width={720}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary disabled={!String(draft.part_number).trim()} onClick={save}>
            {row ? 'Save' : 'Add stock'}
          </Btn>
        </>
      }
    >
      <Msg msg={msg} />
      <Grid cols={3}>
        <Field label="Part number">
          <TextInput aria-label="Stock part number" value={draft.part_number} onChange={set('part_number')} placeholder="In the catalog" />
        </Field>
        {!row && (
          <Field label="Quantity">
            <NumInput aria-label="Quantity" value={draft.qty_on_hand} onChange={(v) => set('qty_on_hand')(v.replace(/\D/g, ''))} />
          </Field>
        )}
        <Field label="Location (shelf / bin)">
          <TextInput aria-label="Location" value={draft.location} onChange={set('location')} />
        </Field>
        <Field label="Condition">
          <SelectInput aria-label="Condition" value={draft.condition} onChange={set('condition')} options={CONDITIONS} />
        </Field>
        <Field label="Unit cost">
          <NumInput aria-label="Unit cost" value={draft.unit_cost} onChange={set('unit_cost')} />
        </Field>
        <Field label="Received">
          <TextInput type="date" aria-label="Received" value={draft.received_at} onChange={set('received_at')} />
        </Field>
        {projects.length > 0 && (
          <Field label="Bought for (GlobalOps project)" span={2}>
            <SelectInput
              aria-label="Bought for project"
              value={draft.origin_project_id}
              onChange={set('origin_project_id')}
              blank="None"
              options={projects.map((p) => ({ value: p.id, label: projectLabel(p) || p.id }))}
            />
          </Field>
        )}
        <Field label="Project number">
          <TextInput aria-label="Origin project number" value={draft.origin_project_number} onChange={set('origin_project_number')} />
        </Field>
        {!row && (
          <Field label="Why it came in" span={3}>
            <TextInput
              aria-label="Why it came in"
              value={draft.reason}
              onChange={set('reason')}
              placeholder="Left over from GAT-5300 (default: Received into stock)"
            />
          </Field>
        )}
        <Field label="Notes" span={3}>
          <textarea
            aria-label="Stock notes"
            value={draft.notes}
            onChange={(e) => set('notes')(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
      </Grid>
    </Modal>
  );
}

/** Stock in or out: how many, and why. */
function MoveEditor({ row, onClose, onSaved }) {
  const [draft, setDraft] = useState({ direction: 'out', qty: '1', reason: '', project_number: '' });
  const [msg, setMsg] = useState(null);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    const res = await moveStock(row.id, draft);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not move the stock.') });
      return;
    }
    const m = res.body.move;
    onSaved(`${m.direction === 'in' ? 'Added' : 'Took out'} ${m.qty} x ${m.part_number}: ${m.qty_after} on hand.`);
  };
  return (
    <Modal
      title={`${row.part_number}: ${row.qty_on_hand} on hand${row.location ? ` at ${row.location}` : ''}`}
      onClose={onClose}
      width={560}
      footer={
        <>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn primary disabled={!draft.reason.trim() || !Number(draft.qty)} onClick={save}>
            Record the move
          </Btn>
        </>
      }
    >
      <Msg msg={msg} />
      <Grid cols={2}>
        <Field label="Move">
          <SelectInput
            aria-label="Move"
            value={draft.direction}
            onChange={set('direction')}
            options={[
              { value: 'out', label: 'Out (sold, used, shipped)' },
              { value: 'in', label: 'In (received, returned)' },
            ]}
          />
        </Field>
        <Field label="Quantity">
          <NumInput aria-label="Move quantity" value={draft.qty} onChange={(v) => set('qty')(v.replace(/\D/g, ''))} />
        </Field>
        <Field label="Why" span={2}>
          <TextInput
            aria-label="Why"
            value={draft.reason}
            onChange={set('reason')}
            placeholder="Sold to Lucid on PO 4500101002 / count correction ..."
          />
        </Field>
        <Field label="Project number (optional)" span={2}>
          <TextInput aria-label="Move project number" value={draft.project_number} onChange={set('project_number')} />
        </Field>
      </Grid>
    </Modal>
  );
}
